import { LEFT_NAV_WIDTH } from "@/common/components/Navigation/RailMenu/consts";

/**
 * Where Ask Genie sits relative to the page — GEN5-5222.
 *
 * Overlay is the original layout: Genie floats over the page. Side by side gives
 * Genie its own column and lays the page out to its left. The rules every
 * component follows are in plans/GEN5-5222/PRINCIPLES.md.
 */

export const ASK_GENIE_LAYOUT_MODE = {
  OVERLAY: "overlay",
  SIDE_BY_SIDE: "sideBySide",
} as const;

export type AskGenieLayoutMode =
  (typeof ASK_GENIE_LAYOUT_MODE)[keyof typeof ASK_GENIE_LAYOUT_MODE];

/** Firestore holds whatever an older build wrote; anything unrecognised is the default. */
export const parseAskGenieLayoutMode = (value: unknown): AskGenieLayoutMode =>
  value === ASK_GENIE_LAYOUT_MODE.SIDE_BY_SIDE
    ? ASK_GENIE_LAYOUT_MODE.SIDE_BY_SIDE
    : ASK_GENIE_LAYOUT_MODE.OVERLAY;

/** Genie never docks narrower than it shipped at (useAskGenieDockedWidth). */
export const GENIE_MIN_DOCKED_WIDTH = 688;
/** The narrowest page the app is laid out for beside Genie. */
const PAGE_MIN_WIDTH = 640;

/** Room the page needs to the left of Genie: the rail plus the narrowest page. */
export const GENIE_PAGE_RESERVED_WIDTH = LEFT_NAV_WIDTH + PAGE_MIN_WIDTH;

/** Below this viewport width side by side is not offered, and Genie overlays the page. */
export const GENIE_SIDE_BY_SIDE_MIN_VIEWPORT =
  GENIE_PAGE_RESERVED_WIDTH + GENIE_MIN_DOCKED_WIDTH;

/** Written on `:root`; `0px` unless the page is laid out beside Genie. */
export const GENIE_PAGE_RIGHT_INSET_VARIABLE = "--genie-page-right-inset";

/**
 * Genie's docked width lives in CSS rather than React state, so a resize drag
 * moves the panel — and, side by side, the page — without re-rendering either.
 */
export const ASK_GENIE_WIDTH_CSS_VARIABLE = "--ask-genie-docked-width";

/** Present on `<html>` only while the user drags Genie's edge; transitions turn off under it. */
export const ASK_GENIE_RESIZING_ATTRIBUTE = "data-ask-genie-resizing";

/**
 * Genie's column width, side by side. The same expression sizes the panel and
 * insets the page, so the two can never disagree — even mid-drag, or when the
 * window shrinks below a width the user saved on a larger screen.
 */
export const genieSideBySideWidth = (pageReservedWidth: number): string =>
  `min(var(${ASK_GENIE_WIDTH_CSS_VARIABLE}, ${GENIE_MIN_DOCKED_WIDTH}px), calc(100vw - ${pageReservedWidth}px))`;

export const GENIE_SIDE_BY_SIDE_WIDTH = genieSideBySideWidth(
  GENIE_PAGE_RESERVED_WIDTH
);

/**
 * Side by side, Genie sits below every overlay the app opens — including the
 * popovers and toolbars that hard-code `zIndex: 1000` — so one that spills over
 * Genie's column is drawn over it, never hidden under it (PRINCIPLES P5). The
 * page and its chrome never reach Genie's column, so nothing needs to sit lower.
 */
export const GENIE_SIDE_BY_SIDE_Z_INDEX = 999;

/** `right` (or a right offset) for anything positioned against the viewport that belongs to the page. */
export const pageRightInset = (offset: number): string =>
  `calc(${offset}px + var(${GENIE_PAGE_RIGHT_INSET_VARIABLE}, 0px))`;

export type GenieDockState =
  | { kind: "closed" }
  | { kind: "overlay" }
  | { kind: "sideBySide"; isMaximized: boolean };

export const deriveGenieDockState = ({
  isOpened,
  layoutMode,
  canShowSideBySide,
  isMaximized,
}: {
  isOpened: boolean;
  layoutMode: AskGenieLayoutMode;
  canShowSideBySide: boolean;
  isMaximized: boolean;
}): GenieDockState => {
  if (!isOpened) return { kind: "closed" };
  if (layoutMode !== ASK_GENIE_LAYOUT_MODE.SIDE_BY_SIDE || !canShowSideBySide) {
    return { kind: "overlay" };
  }
  return { kind: "sideBySide", isMaximized };
};

/** The page gives up its right edge only while Genie sits beside it, not over it. */
export const genieDockPageInset = (dockState: GenieDockState): string =>
  dockState.kind === "sideBySide" && !dockState.isMaximized
    ? GENIE_SIDE_BY_SIDE_WIDTH
    : "0px";

/**
 * On a modal that belongs to Genie (its own dialogs): it opens in Genie's
 * column, never in the page area (PRINCIPLES P4).
 */
export const GENIE_SURFACE_CLASS = "genie-surface";

/**
 * On a modal that deliberately spans the whole viewport, Genie included: an
 * explicit full screen, or a flow too large for either column (PRINCIPLES P4).
 */
export const GENIE_COVER_CLASS = "genie-cover";

/**
 * A modal's root and its backdrop. The backdrop is `position: fixed` itself,
 * so it is placed against the viewport, not the root, and is inset on its own.
 */
const withBackdrop = (roots: string[]): string =>
  roots.flatMap((root) => [root, `${root} > .MuiBackdrop-root`]).join(", ");

/** Every page modal — dialogs and temporary drawers, from any page or library. */
export const PAGE_MODAL_SELECTOR = withBackdrop(
  [".MuiDialog-root", ".MuiDrawer-modal"].map(
    (root) => `${root}:not(.${GENIE_SURFACE_CLASS}):not(.${GENIE_COVER_CLASS})`
  )
);

/** Genie's own modals. */
export const GENIE_SURFACE_MODAL_SELECTOR = withBackdrop([
  `.MuiDialog-root.${GENIE_SURFACE_CLASS}`,
  `.MuiDrawer-modal.${GENIE_SURFACE_CLASS}`,
]);

/** Prefixes each selector so it applies only while Genie's edge is being dragged. */
export const whileResizingGenie = (selector: string): string =>
  selector
    .split(", ")
    .map((part) => `html[${ASK_GENIE_RESIZING_ATTRIBUTE}] ${part}`)
    .join(", ");
